import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { DiscountManagementInjection } from "features/discountManagement/di/DiscountManagementInjection";
import { injectable } from "tsyringe";
import { paramsChecking } from "utils/helpersInTs/helpersInTs";
import { DiscountManagementFormRequestEntity } from "../entities/DiscountManagementFormRequestEntity";
import { DiscountRequestEntity } from "../entities/DiscountRequestEntity";
import { DiscountResponseEntity, UserInDiscountEntity } from "../entities/DiscountResponseEntity";
import { IDiscountManagementRepository } from "../repositories/IDiscountManagementRepository";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { FILTER_FEATURE, DISCOUNT_MANAGEMENT } from "api/ApiPath";

@injectable()
export class DiscountManagementUseCase {
  private discountRepo: IDiscountManagementRepository;
  private filterUseCase = FilterInjection.getUseCase();

  constructor() {
    // Note: getDiscountManagementRepo won't work until I update Injection.ts
    // For now I'll write the new one which we'll update in Injection
    this.discountRepo = DiscountManagementInjection.getDiscountManagementRepo();
  }

  private checkParams(params: DiscountRequestEntity): DiscountRequestEntity {
    return {
      ...params,
      status: paramsChecking(params?.status, "select"),
      service: paramsChecking(params?.service, "select"),
      group: paramsChecking(params?.group, "select"),
      product: paramsChecking(params?.product?.["code"], "select"),
    };
  }

  private adjustCreateBody(values: any): DiscountManagementFormRequestEntity {
    return {
      ...values,
      type: Number(values?.type),
      status: Number(values?.status),
    };
  }

  private adjustUpdateBody(
    values: any,
    currentUsers: UserInDiscountEntity[],
  ): DiscountManagementFormRequestEntity {
    return {
      ...values,
      code: values?.code,
      type: Number(values?.type),
      status: Number(values?.status),
      users: this.mergeUsers(values?.users || [], currentUsers || []),
    };
  }

  private mergeUsers(
    newUsers: Array<{ phone: string; status: number; discountRate: string }>,
    currentUsers: UserInDiscountEntity[],
  ): Array<{ phone: string; status: number; discountRate: string }> {
    const finalUsers = [...newUsers];
    const phoneSet = new Set<string>();

    for (const user of newUsers) {
      if (user.phone) phoneSet.add(user.phone);
    }

    for (const user of currentUsers) {
      if (user.userPhone && !phoneSet.has(user.userPhone)) {
        finalUsers.push({
          phone: user.userPhone,
          discountRate: user.discountRate || "",
          status: -1,
        });
      }
    }

    return finalUsers;
  }

  async getDiscounts(
    params: DiscountRequestEntity,
  ): Promise<ListBaseApiResponseModel<DiscountResponseEntity>> {
    return await this.discountRepo.getList(this.checkParams(params));
  }

  async getDiscountDetail(
    code: string,
  ): Promise<BaseApiResponseModel<DiscountResponseEntity>> {
    return await this.discountRepo.getDetail(code);
  }

  async createDiscount(
    values: any,
  ): Promise<BaseApiResponseModel<DiscountResponseEntity>> {
    return await this.discountRepo.create(this.adjustCreateBody(values));
  }

  async updateDiscount(
    values: any,
    currentUsers: UserInDiscountEntity[],
  ): Promise<BaseApiResponseModel<DiscountResponseEntity>> {
    return await this.discountRepo.update(
      this.adjustUpdateBody(values, currentUsers),
    );
  }

  async deleteDiscount(
    code: string,
  ): Promise<BaseApiResponseModel<DiscountResponseEntity>> {
    return await this.discountRepo.update({ code, status: -1 } as DiscountManagementFormRequestEntity);
  }

  // Filter and lookup APIs going through FilterUseCase
  async fetchServiceList(params: BaseFilterListRequestModel) {
    return this.filterUseCase.fetchListWithFeature<BaseFilterListRequestModel, BaseFilterListResponseModel>({
      path: FILTER_FEATURE.SERVICE_LIST,
      params,
    });
  }

  async fetchGroupList(params: BaseFilterListRequestModel & { service?: any }) {
    return this.filterUseCase.fetchListWithFeature<BaseFilterListRequestModel, BaseFilterListResponseModel>({
      path: DISCOUNT_MANAGEMENT.GROUP_LIST,
      params: { ...params, service: params?.service === "All" ? undefined : params?.service }
    });
  }

  async fetchProductList(params: BaseFilterListRequestModel & { service?: any, group?: any }) {
    return this.filterUseCase.fetchListAndPagingWithFeature<BaseFilterListRequestModel, BaseFilterListResponseModel>({
      path: DISCOUNT_MANAGEMENT.PRODUCT_LIST,
      params: {
        ...params,
        service: params?.service === "All" ? undefined : params?.service,
        group: params?.group === "All" ? undefined : params?.group,
      }
    });
  }
}
